// Classifies cluster-level health from fields already joined onto the
// kaapiClustersOverviewSelector row: Cluster.status, hostedControlPlane
// (Kamaji HCP) and kubeadmControlPlane (CCP only). Mirrors the machine
// classifier in ../machines/machineHealth.ts.
import { MachineHealth, MachineHealthState } from '../machines/machineHealth'

// Cluster reconciliation legitimately takes longer than a single machine;
// only surface non-Error conditions once the cluster is 10 minutes old.
export const CLUSTER_CONDITION_GRACE_MS = 10 * 60 * 1000

const CLUSTER_CONDITION_TYPES = [
  'TopologyReconciled',
  'ControlPlaneReady',
  'InfrastructureReady',
  'CCPFRPSReady',
  'RemoteConnectionProbe',
]

// CCPFRPSReady/RemoteConnectionProbe reflect the tunnel to a control plane
// that isn't up yet - not a connectivity loss - until ControlPlaneInitialized
// fires at least once. Mirrors hasControlPlaneConnectionIssue in
// ../machines/machineHealth.ts, which gates the same two conditions the same way.
const CONNECTIVITY_CONDITION_TYPES = ['CCPFRPSReady', 'RemoteConnectionProbe']
const KCP_CONDITION_TYPES = [
  'Available',
  'ControlPlaneComponentsHealthy',
  'EtcdClusterHealthy',
  'CertificatesAvailable',
]

// Progress reasons describe an in-flight reconcile (control plane still
// scaling, waiting for a dependency), not a failure. They must not be escalated
// to an error just because the grace period elapsed - the actionable cause
// (e.g. a node VM-create failure) surfaces at the node level instead.
const PROGRESS_REASON = /^(ScalingUp|ScalingDown|Scaling|WaitingFor)/

const findErrorCondition = (conditions: any[], types: string[], age: number) =>
  conditions?.find(
    (c) =>
      c?.status === 'False' && c?.severity === 'Error' && c?.message && types.includes(c?.type),
  ) ||
  (age > CLUSTER_CONDITION_GRACE_MS
    ? conditions?.find(
        (c) =>
          c?.status === 'False' &&
          c?.message &&
          types.includes(c?.type) &&
          !PROGRESS_REASON.test(c?.reason || ''),
      )
    : undefined)

// A cluster-infra Warning event recurs every 1-2 minutes while the failure
// is live (e.g. security-group quota) and stops once it resolves; lingering
// events expire on the apiserver's default TTL (~1h). Only treat one as an
// active failure if it fired recently, so a recovered cluster clears.
export const INFRA_EVENT_STALE_MS = 15 * 60 * 1000

export function getClusterHealth(
  cluster,
  now: number = Date.now(),
  infraFailureEvent?,
): MachineHealth {
  const status = cluster?.status

  if (status?.failureReason || status?.failureMessage) {
    return {
      state: MachineHealthState.Failed,
      reason: status?.failureReason || 'ClusterFailed',
      message: status?.failureMessage || 'Cluster entered a terminal failed state',
    }
  }

  if (status?.phase === 'Deleting' || cluster?.metadata?.deletionTimestamp) {
    return { state: MachineHealthState.Progressing }
  }

  const createdAt = Date.parse(cluster?.metadata?.creationTimestamp)
  const age = Number.isFinite(createdAt) ? now - createdAt : 0

  const allConditions = [...(status?.conditions || []), ...(status?.v1beta2?.conditions || [])]
  const controlPlaneWasInitialized = allConditions.some(
    (c) => c?.type === 'ControlPlaneInitialized' && c?.status === 'True',
  )
  const clusterConditions = controlPlaneWasInitialized
    ? allConditions
    : allConditions.filter((c) => !CONNECTIVITY_CONDITION_TYPES.includes(c?.type))

  // CAPO never writes a status condition for cluster-infra failures like
  // security-group quota exceeded - the only signal is a recurring Warning
  // Event on the OpenStackCluster (passed in as infraFailureEvent, matched
  // by the cluster's infrastructureRef name). Surface it once past grace and
  // while still recent, ahead of the condition scan below: once infra is
  // blocked this way, downstream conditions like RemoteConnectionProbe also
  // go False, but the event is the actionable cause and must win.
  const eventAge = infraFailureEvent ? now - Date.parse(infraFailureEvent?.lastTimestamp) : Infinity
  if (age > CLUSTER_CONDITION_GRACE_MS && infraFailureEvent && eventAge < INFRA_EVENT_STALE_MS) {
    return {
      state: MachineHealthState.Degraded,
      reason: infraFailureEvent.reason || 'InfrastructureError',
      message: infraFailureEvent.message,
    }
  }

  const clusterCondition = findErrorCondition(clusterConditions, CLUSTER_CONDITION_TYPES, age)
  if (clusterCondition) {
    return {
      state: MachineHealthState.Degraded,
      reason: clusterCondition.reason || clusterCondition.type,
      message: clusterCondition.message,
    }
  }

  // During grace period, any False condition in our allowlist means Progressing
  if (age < CLUSTER_CONDITION_GRACE_MS) {
    const anyFalseCondition = clusterConditions.find(
      (c) => c?.status === 'False' && CLUSTER_CONDITION_TYPES.includes(c?.type),
    )
    if (anyFalseCondition) {
      return { state: MachineHealthState.Progressing }
    }
  }

  const hcpStatus = cluster?.hostedControlPlane?.status
  if (hcpStatus?.failureMessage) {
    return {
      state: MachineHealthState.Degraded,
      reason: hcpStatus?.failureReason || 'ControlPlaneError',
      message: hcpStatus.failureMessage,
    }
  }

  const kcpCondition = findErrorCondition(
    cluster?.kubeadmControlPlane?.status?.conditions,
    KCP_CONDITION_TYPES,
    age,
  )
  if (kcpCondition) {
    return {
      state: MachineHealthState.Degraded,
      reason: kcpCondition.reason || kcpCondition.type,
      message: kcpCondition.message,
    }
  }

  return { state: MachineHealthState.Ok }
}
